import { useRef, useState } from "react"

import { PostAuthSignupBody as FormSchemaBase } from "@boundless/bl-api-client/allauth/allauth.zod"
import {
  usePostAuthSignup,
  type PostAuthSignupMutationBody,
} from "@boundless/bl-api-client/allauth/auth"
import { EstimatedRevenueEnumOptions } from "@boundless/bl-api-client/customer/models/EstimatedRevenueEnumLabels"
import { usePublicBoundlessConfigRetrieveSuspense } from "@boundless/bl-api-client/public/services"
import { zodResolver } from "@hookform/resolvers/zod"
import { useForm, useWatch } from "react-hook-form"
import { z as zod, z } from "zod"

import {
  StickyFormActions,
  useIsInAccountFormColumn,
} from "@/components/account/layout/StickyFormActions"
import { FormCheckbox } from "@/components/form-elements/FormCheckbox"
import { FormConsentText } from "@/components/form-elements/FormConsentText"
import { FormInput } from "@/components/form-elements/FormInput"
import { FormInputPhone } from "@/components/form-elements/FormInputPhone"
import { FormSelect } from "@/components/form-elements/FormSelect"
import {
  FormTurnstile,
  type FormTurnstileHandle,
} from "@/components/form-elements/FormTurnstile"
import { Button } from "@/components/ui/button"
import { Form } from "@/components/ui/form"
import type { EmbedTurnstileFrameConfig } from "@/embed/turnstile-frame-context"
import {
  isAccountPendingEmailVerificationError,
  isEmbedAccountExistsError,
} from "@/lib/account/utils"
import { getAuthClient } from "@/lib/clients"
import { applyErrorsToForm } from "@/lib/forms/utils"
import { cn } from "@/lib/utils"

export function SignupForm({
  onSuccess,
  onSignInClick,
  slug,
  embedUserId,
  prefill,
  turnstileFrame,
}: React.ComponentPropsWithoutRef<"form"> & {
  onSuccess: (email: string) => void
  onSignInClick?: () => void
  slug?: string
  embedUserId?: string
  turnstileFrame?: EmbedTurnstileFrameConfig
  prefill?: {
    email?: string | null
    firstName?: string | null
    lastName?: string | null
  }
}) {
  const client = getAuthClient()
  const isInAccountLayout = useIsInAccountFormColumn()
  const { data: config } = usePublicBoundlessConfigRetrieveSuspense()

  // Code-first by default: the emailed code verifies and is the credential.
  // "Use a password instead" reveals the password fields. The embed flow is
  // code-only, so the password option is hidden there.
  const [usePassword, setUsePassword] = useState(false)
  const turnstileRef = useRef<FormTurnstileHandle>(null)
  // The email of an Embed signup rejected because the user already has an
  // account. Kept out of the form's root error so the message can hide as
  // soon as the email is edited.
  const [existingAccountEmail, setExistingAccountEmail] = useState<
    string | null
  >(null)
  const allowPasswordOption = !embedUserId
  const turnstileEnabled =
    config.signupCaptchaEnabled &&
    (Boolean(turnstileFrame) || Boolean(config.turnstileSiteKey))

  const FormSchema = FormSchemaBase.and(
    zod.object({ password_confirm: zod.string() }),
  )

  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      email: prefill?.email ?? "",
      password: "",
      password_confirm: "",
      first_name: prefill?.firstName ?? "",
      last_name: prefill?.lastName ?? "",
      phone: "",
      estimated_revenue: "",
      has_accepted_terms: false,
      captcha: "",
    },
  })
  const formValues = useWatch({ control: form.control })
  const hasCompletedRequiredFields = Boolean(
    formValues.email?.trim() &&
    formValues.first_name?.trim() &&
    formValues.last_name?.trim() &&
    formValues.phone?.trim() &&
    formValues.estimated_revenue?.trim() &&
    formValues.has_accepted_terms &&
    (!turnstileEnabled || formValues.captcha) &&
    (!usePassword || (formValues.password && formValues.password_confirm)),
  )
  const showExistingAccount =
    existingAccountEmail !== null && formValues.email === existingAccountEmail

  const { isPending, mutate: signup } = usePostAuthSignup({
    mutation: {
      onError: (error, { data }) => {
        // By default, allauth automatically logs the user in after signup.
        // However, since we use mandatory email verification, it instead
        // returns a 401 with the `verify_email` flow in pending status.
        // We check for this specific case and navigate to the page displaying
        // a success message to verify the email.
        if (isAccountPendingEmailVerificationError(error)) {
          return onSuccess(form.getValues("email"))
        }

        turnstileRef.current?.reset()

        if (isEmbedAccountExistsError(error)) {
          return setExistingAccountEmail(data.email)
        }

        applyErrorsToForm(form.setError, error)
      },
    },
  })

  function handleSubmit(data: z.infer<typeof FormSchema>) {
    setExistingAccountEmail(null)
    const { password_confirm, captcha, password, ...rest } = data
    const payload: PostAuthSignupMutationBody = {
      ...rest,
      partner: slug,
      captcha: captcha ?? "",
    }

    // Passwordless by default: omit the password entirely so allauth creates
    // a passwordless account (the code is the credential). Only the password
    // path sends one, after confirming the two entries match.
    if (usePassword) {
      if (password !== password_confirm) {
        form.setError("password_confirm", {
          type: "manual",
          message: "Passwords do not match",
        })
        return
      }
      payload.password = password
    }

    if (embedUserId) {
      payload.embed_user_id = embedUserId
    }

    signup({ client, data: payload })
  }

  return (
    <Form {...form}>
      <form
        className="flex flex-1 flex-col"
        onSubmit={form.handleSubmit(handleSubmit)}
      >
        <div
          className={cn(
            "flex flex-1 flex-col gap-4",
            isInAccountLayout && "lg:gap-3",
          )}
        >
          <FormInput
            name="email"
            placeholder="Email"
            type="email"
            autoComplete="email"
          />

          {/* The account page has to fit a laptop screen without scrolling,
              so the name fields share a row there. Dialogs keep them stacked. */}
          <div
            className={cn(
              "flex flex-col gap-4",
              isInAccountLayout && "lg:grid lg:grid-cols-2 lg:gap-3",
            )}
          >
            <FormInput
              name="first_name"
              placeholder="First name"
              autoComplete="given-name"
            />
            <FormInput
              name="last_name"
              placeholder="Last name"
              autoComplete="family-name"
            />
          </div>
          <FormInputPhone
            name="phone"
            placeholder="Phone number"
            autoComplete="tel"
          />
          <FormSelect
            name="estimated_revenue"
            options={EstimatedRevenueEnumOptions}
            placeholder="Average monthly revenue"
          />
          {usePassword && (
            <>
              <FormInput
                name="password"
                placeholder="Password"
                type="password"
                autoComplete="new-password"
              />
              <FormInput
                name="password_confirm"
                placeholder="Confirm password"
                type="password"
                autoComplete="new-password"
              />
            </>
          )}

          <div className="px-8 py-2">
            <FormCheckbox
              name="has_accepted_terms"
              className="size-6 self-center"
              messageClassName="text-center"
              label={<FormConsentText action="checking this box" />}
            />
          </div>

          {turnstileEnabled &&
            (turnstileFrame ? (
              <FormTurnstile
                ref={turnstileRef}
                action="signup"
                className="mx-auto pb-4"
                name="captcha"
                frame={turnstileFrame}
              />
            ) : (
              <FormTurnstile
                ref={turnstileRef}
                action="signup"
                className={cn("mx-auto", !isInAccountLayout && "pb-4")}
                name="captcha"
                siteKey={config.turnstileSiteKey}
                // Most borrowers pass without a challenge, so on the account
                // page the widget only takes up room when one is needed.
                appearance={isInAccountLayout ? "interaction-only" : "always"}
              />
            ))}

          {form.formState.errors.root && (
            <p className="text-destructive text-center text-sm font-medium">
              {form.formState.errors.root.message}
            </p>
          )}

          {showExistingAccount && (
            <div className="flex flex-col items-center gap-3">
              <p className="text-destructive text-center text-sm font-medium">
                You already have an account. Sign in to continue.
              </p>
              {onSignInClick && (
                <Button
                  type="button"
                  variant="outline"
                  className="w-36"
                  onClick={onSignInClick}
                >
                  Sign in
                </Button>
              )}
            </div>
          )}

          <StickyFormActions>
            <Button
              type="submit"
              disabled={
                isPending || !hasCompletedRequiredFields || showExistingAccount
              }
            >
              {isPending
                ? "Submitting..."
                : usePassword
                  ? "Continue"
                  : "Send code & continue"}
            </Button>

            {allowPasswordOption && (
              <div className="text-center">
                <Button
                  variant="link"
                  type="button"
                  className="p-0 text-neutral-600 hover:text-neutral-800"
                  onClick={() => setUsePassword((prev) => !prev)}
                >
                  {usePassword
                    ? "Use email code instead"
                    : "Use a password instead"}
                </Button>
              </div>
            )}
          </StickyFormActions>
        </div>
      </form>
    </Form>
  )
}
